import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralService } from '../../services/referralService';
import { practitionerService } from '../../services/practitionerService';
import { dataManagementService } from '../../services/dataManagementService';
import { supabase } from '../../lib/supabase';
import StatisticsCards from '../referral-management-dashboard/components/StatisticsCards';
import FilterPanel from '../referral-management-dashboard/components/FilterPanel';
import ReferralTable from '../referral-management-dashboard/components/ReferralTable';
import ReferralDetailModal from '../referral-management-dashboard/components/ReferralDetailModal';
import ClearDataModal from './components/ClearDataModal';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


const IndividualPractitionerDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [practitioner, setPractitioner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearDataLoading, setClearDataLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
    referringDentist: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'submissionDate',
    direction: 'desc'
  });

  // Load practitioner data and referrals
  useEffect(() => {
    if (!authLoading && userProfile) {
      // Ensure user has practitioner_id
      if (!userProfile?.practitionerId) {
        setError('Aucun praticien associé à ce compte');
        setLoading(false);
        return;
      }
      loadData();
    }
  }, [authLoading, userProfile]);

  // Real-time subscription for new referrals
  useEffect(() => {
    if (!userProfile?.practitionerId) return;

    const channel = supabase?.channel('practitioner-referrals-changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'referrals',
          filter: `practitioner_id=eq.${userProfile?.practitionerId}`
        },
        (payload) => {
          const newReferral = {
            id: payload?.new?.id,
            practitionerId: payload?.new?.practitioner_id,
            patientFirstName: payload?.new?.patient_first_name,
            patientLastName: payload?.new?.patient_last_name,
            patientYearOfBirth: payload?.new?.patient_year_of_birth,
            patientSex: payload?.new?.patient_sex,
            patientPhone: payload?.new?.patient_phone,
            patientEmail: payload?.new?.patient_email,
            referringDentistName: payload?.new?.referring_dentist_name,
            referringPracticeName: payload?.new?.referring_practice_name,
            referringDentistEmail: payload?.new?.referring_dentist_email,
            referringDentistPhone: payload?.new?.referring_dentist_phone,
            toothNumbers: payload?.new?.tooth_numbers || [],
            referralReasons: payload?.new?.referral_reasons || [],
            clinicalNotes: payload?.new?.clinical_notes,
            imagingDownloadLink: payload?.new?.imaging_download_link,
            imagingPassword: payload?.new?.imaging_password,
            imagingCenter: payload?.new?.imaging_center,
            status: payload?.new?.status,
            urgency: payload?.new?.urgency,
            submissionDate: payload?.new?.submission_date,
            reviewedAt: payload?.new?.reviewed_at,
            scheduledAt: payload?.new?.scheduled_at,
            completedAt: payload?.new?.completed_at,
            notes: payload?.new?.notes,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };

          setReferrals(prev => [newReferral, ...prev]);
          
          setStatistics(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              totalPending: prev?.totalPending + (newReferral?.status === 'new' || newReferral?.status === 'reviewed' ? 1 : 0),
              urgentCount: prev?.urgentCount + (newReferral?.urgency === 'urgent' || newReferral?.urgency === 'emergency' ? 1 : 0),
              byStatus: {
                ...prev?.byStatus,
                [newReferral?.status]: (prev?.byStatus?.[newReferral?.status] || 0) + 1
              }
            };
          });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [userProfile?.practitionerId]);

  // Apply filters when data changes
  useEffect(() => {
    applyFiltersAndSort();
  }, [referrals, filters, sortConfig]);

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      urgency: 'all',
      referringDentist: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load practitioner details
      const practitionerData = await practitionerService?.getById(userProfile?.practitionerId);
      setPractitioner(practitionerData);

      // Load referrals for this practitioner only
      const referralsData = await referralService?.getAll(userProfile?.practitionerId);
      setReferrals(referralsData);

      // Load statistics for this practitioner
      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...referrals];

    // Apply status filter
    if (filters?.status && filters?.status !== 'all') {
      filtered = filtered?.filter(r => r?.status === filters?.status);
    }

    // Apply urgency filter
    if (filters?.urgency && filters?.urgency !== 'all') {
      filtered = filtered?.filter(r => r?.urgency === filters?.urgency);
    }

    // Apply referring dentist filter
    if (filters?.referringDentist) {
      filtered = filtered?.filter(r => 
        r?.referringDentistName?.toLowerCase()?.includes(filters?.referringDentist?.toLowerCase())
      );
    }

    // Apply date range filters
    if (filters?.dateFrom) {
      filtered = filtered?.filter(r => new Date(r?.submissionDate) >= new Date(filters?.dateFrom));
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(r => new Date(r?.submissionDate) <= new Date(filters?.dateTo));
    }

    // Apply search term
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(r => 
        r?.patientFirstName?.toLowerCase()?.includes(term) ||
        r?.patientLastName?.toLowerCase()?.includes(term) ||
        r?.referringDentistName?.toLowerCase()?.includes(term)
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.field];
      const bValue = b?.[sortConfig?.field];

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig?.direction === 'asc' ? comparison : -comparison;
    });

    setFilteredReferrals(filtered);
  };

  const handleStatusUpdate = async (referralId, newStatus) => {
    try {
      await referralService?.updateStatus(referralId, newStatus);
      
      // Update local state
      setReferrals(prev => prev?.map(r => 
        r?.id === referralId ? { ...r, status: newStatus } : r
      ));

      // Reload statistics
      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleNotesUpdate = async (referralId, notes) => {
    try {
      await referralService?.updateNotes(referralId, notes);
      
      setReferrals(prev => prev?.map(r => 
        r?.id === referralId ? { ...r, notes } : r
      ));
    } catch (err) {
      console.error('Error updating notes:', err);
      alert('Erreur lors de la mise à jour des notes');
    }
  };

  const handleDeleteReferral = async (referralId) => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir supprimer cet adressage ?\n\nCette action est irréversible.'
    );

    if (!confirmed) return;

    try {
      await referralService?.delete(referralId);

      // Update local state
      setReferrals(prev => prev?.filter(r => r?.id !== referralId));

      // Reload statistics
      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);

      alert('✅ Adressage supprimé avec succès');
    } catch (err) {
      console.error('Error deleting referral:', err);
      alert(`❌ Erreur lors de la suppression: ${err?.message}`);
    }
  };

  const handleViewDetails = (referral) => {
    setSelectedReferral(referral);
    setShowDetailModal(true);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect even on error
      navigate('/login');
    }
  };

  const handleNavigateToSettings = () => {
    navigate('/practitioner-management-dashboard');
  };

  const handleClearAllData = async () => {
    try {
      setClearDataLoading(true);
      
      const result = await dataManagementService?.deletePractitionerReferrals(userProfile?.practitionerId);
      
      if (result?.success) {
        // Reload data to reflect changes
        await loadData();
        setShowClearDataModal(false);
        alert(`✅ ${result?.deletedCount} adressage(s) supprimé(s) avec succès`);
      }
    } catch (err) {
      console.error('Error clearing data:', err);
      alert(`❌ Erreur: ${err?.message}`);
    } finally {
      setClearDataLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="alert-circle" className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin-login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  // Use global theme colors instead of per-practitioner customization
  const primaryColor = '#2563eb'; // Global primary color
  const secondaryColor = '#1e40af'; // Global secondary color

  return (
    <>
      <Helmet>
        <title>{practitioner?.practiceName || 'Tableau de Bord'} - Gestion des Référencements</title>
        <meta name="description" content="Tableau de bord personnel pour la gestion des référencements endodontiques" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Header with custom branding */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Show initials logo if configured, otherwise show URL logo */}
                {practitioner?.logoInitials ? (
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: practitioner?.logoBackgroundColor || '#000000' }}
                  >
                    <span className="text-xl font-bold text-white">
                      {practitioner?.logoInitials?.toUpperCase()}
                    </span>
                  </div>
                ) : practitioner?.logoUrl ? (
                  <img 
                    src={practitioner?.logoUrl} 
                    alt={practitioner?.practiceName}
                    className="h-12 w-auto object-contain"
                  />
                ) : null}
                <div>
                  <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>
                    {practitioner?.practiceName}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {practitioner?.practitionerTitle} {practitioner?.practitionerName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleNavigateToSettings}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="settings" className="w-5 h-5" />
                  <span className="hidden sm:inline">Paramètres</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon name="log-out" className="w-5 h-5" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
                <Button
                  onClick={() => setShowClearDataModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors border border-red-200"
                >
                  <Icon name="Trash2" size={18} />
                  <span>Vider mes données</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Statistics Cards */}
          <div className="mb-8">
            <StatisticsCards statistics={statistics} />
          </div>

          {/* Filters */}
          <div className="mb-6">
            <FilterPanel 
              filters={filters}
              onFilterChange={setFilters}
              onClearFilters={handleClearFilters}
            />
          </div>

          {/* Referrals Table */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold" style={{ color: primaryColor }}>
                Mes Référencements ({filteredReferrals?.length})
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Référencements soumis via votre formulaire public
              </p>
            </div>
            <ReferralTable
              referrals={filteredReferrals}
              sortConfig={sortConfig}
              onSort={handleSort}
              onViewDetails={handleViewDetails}
              onStatusUpdate={handleStatusUpdate}
              onDelete={handleDeleteReferral}
              onViewAISummary={() => {}}
              onUrgencyUpdate={async () => {}}
            />
          </div>
        </main>

        <Footer />
      </div>

      {/* Modals */}
      {showDetailModal && selectedReferral && (
        <ReferralDetailModal
          referral={selectedReferral}
          onClose={() => setShowDetailModal(false)}
          onStatusUpdate={handleStatusUpdate}
          onNotesUpdate={handleNotesUpdate}
          onRefresh={loadData}
        />
      )}

      {showClearDataModal && (
        <ClearDataModal
          isOpen={showClearDataModal}
          onClose={() => setShowClearDataModal(false)}
          onConfirm={handleClearAllData}
          practitionerName={practitioner?.practitionerName || 'ce praticien'}
          loading={clearDataLoading}
        />
      )}
    </>
  );
};

export default IndividualPractitionerDashboard;